import { useState, useEffect } from "react";
import { Loader2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface RoomExamLoadingProps {
  onComplete?: () => void;
}

const loadingSteps = [
  "Preparing your exam materials",
  "Analyzing existing contents",
  "Generating question types",
  "Structuring exam preference",
  "Reviewing final criteria"
];

export const RoomExamLoading = ({ onComplete }: RoomExamLoadingProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];

    loadingSteps.forEach((_, index) => {
      const timeout = setTimeout(() => {
        if (index < loadingSteps.length - 1) {
          setCompletedSteps(prev => [...prev, index]);
          setCurrentStep(index + 1);
        } else {
          setCompletedSteps(prev => [...prev, index]);
        }
      }, (index + 1) * 2500);

      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  useEffect(() => {
    if (completedSteps.length === loadingSteps.length && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [completedSteps.length, onComplete]);

  const getStepState = (stepIndex: number) => {
    if (completedSteps.includes(stepIndex)) {
      return "completed";
    } else if (stepIndex === currentStep) {
      return "loading";
    }
    return "pending";
  };

  const getStepIcon = (stepIndex: number) => {
    const state = getStepState(stepIndex);

    switch (state) {
      case "loading":
        return <Loader2 className="h-5 w-5 mr-3 animate-spin text-foreground" />;
      case "completed":
        return <Check className="h-5 w-5 mr-3 text-primary" />;
      default:
        return <div className="h-5 w-5 mr-3" />;
    }
  };

  const getStepTextColor = (stepIndex: number) => {
    const state = getStepState(stepIndex);
    return state === "pending" ? "text-muted-foreground" : "text-foreground";
  };

  return (
    <section className="flex w-full items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex min-h-[280px] w-full flex-col items-center justify-center gap-6 rounded-3xl border border-border bg-card px-8 py-10 shadow-sm"
      >
        <div className="space-y-4 w-full max-w-sm">
          {loadingSteps.map((step, index) => {
            const state = getStepState(index);
            const isVisible = state === "loading" || state === "completed";
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{
                  opacity: isVisible ? 1 : 0.4,
                  x: isVisible ? 0 : -10
                }}
                transition={{ duration: 0.3, delay: isVisible ? 0.1 : 0 }}
                className="flex items-center"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={state}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {getStepIcon(index)}
                  </motion.div>
                </AnimatePresence>
                <span className={`text-lg ${getStepTextColor(index)}`}>
                  {step}
                </span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};
